import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi, clientsApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { CodePill } from "@/components/Atoms";
import { Plus, Trash2, Pencil, Search, Save, Shield } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "service", "operator", "viewer"];

const TABS = [
  { k: "users", label: "Users" },
  { k: "scopes", label: "Operator scopes" },
];

export default function Users() {
  const [tab, setTab] = useState("users");
  return (
    <PageContainer>
      <PageHeader
        title="Users"
        subtitle="Manage bearer-token accounts and per-operator client scopes. Admin-only."
      />
      <div className="flex gap-1 border-b border-zinc-200 mb-4" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`users-tab-${t.k}`}
            role="tab"
            aria-selected={tab === t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.k
                ? "border-[#3730A3] text-zinc-900"
                : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "users" ? <UsersTab /> : <ScopesTab />}
    </PageContainer>
  );
}

// -----------------------------------------------------------------------------
// Tab 1 — Users CRUD
// -----------------------------------------------------------------------------
function UsersTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // { mode, row }

  const list = useQuery({ queryKey: ["users"], queryFn: usersApi.list });
  const inval = () => qc.invalidateQueries({ queryKey: ["users"] });

  const createM = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      toast.success("User created");
      inval();
      setModal(null);
    },
  });
  const updateM = useMutation({
    mutationFn: ({ id, data }) => usersApi.update(id, data),
    onSuccess: () => {
      toast.success("User updated");
      inval();
      setModal(null);
    },
  });
  const removeM = useMutation({
    mutationFn: usersApi.remove,
    onSuccess: () => {
      toast.success("User deleted");
      inval();
    },
  });

  const rows = list.data || [];
  const filtered = search
    ? rows.filter(
        (u) =>
          (u.username || "").toLowerCase().includes(search.toLowerCase()) ||
          (u.role || "").toLowerCase().includes(search.toLowerCase())
      )
    : rows;
  const pager = usePager(filtered, { defaultSize: 50, sizes: [25, 50, 100] });

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
          <input
            data-testid="user-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search username or role…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
          />
        </div>
        <div className="flex-1" />
        <button
          data-testid="user-new"
          onClick={() => setModal({ mode: "create" })}
          className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-indigo-900 inline-flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> New user
        </button>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left p-3 font-semibold">Username</th>
            <th className="text-left p-3 font-semibold">Role</th>
            <th className="text-left p-3 font-semibold">Scope</th>
            <th className="text-left p-3 font-semibold">Created</th>
            <th className="w-24"></th>
          </tr>
        </thead>
        <tbody>
          {pager.pageRows.map((u) => (
            <tr
              key={u.id || u.username}
              className="border-t border-zinc-100 hover:bg-zinc-50/60"
              data-testid={`user-row-${u.username}`}
            >
              <td className="p-3 font-medium">{u.username}</td>
              <td className="p-3">
                <CodePill>{u.role}</CodePill>
              </td>
              <td className="p-3 text-zinc-500 text-xs font-mono">
                {u.role === "operator"
                  ? `${(u.client_ids || []).length} client${(u.client_ids || []).length === 1 ? "" : "s"}`
                  : "—"}
              </td>
              <td className="p-3 text-xs text-zinc-500 font-mono">
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
              </td>
              <td className="p-3">
                <div className="flex items-center justify-end gap-1">
                  <button
                    data-testid={`user-edit-${u.username}`}
                    onClick={() => setModal({ mode: "edit", row: u })}
                    className="p-1.5 rounded hover:bg-zinc-100"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`user-delete-${u.username}`}
                    onClick={() => {
                      if (window.confirm(`Delete user "${u.username}"?`)) removeM.mutate(u.id);
                    }}
                    className="p-1.5 rounded hover:bg-red-50 text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {pager.total === 0 && (
            <tr>
              <td colSpan={5} className="py-10 text-center text-sm text-zinc-500">
                No users found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <Pager {...pager} testidPrefix="user-pager" />

      {modal && (
        <UserModal
          mode={modal.mode}
          user={modal.row}
          onCancel={() => setModal(null)}
          onSave={(form) =>
            modal.mode === "create"
              ? createM.mutate(form)
              : updateM.mutate({ id: modal.row.id, data: form })
          }
        />
      )}
    </div>
  );
}

function UserModal({ mode, user, onCancel, onSave }) {
  const isEdit = mode === "edit";
  const clients = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });
  const [form, setForm] = useState({
    username: user?.username || "",
    password: "",
    role: user?.role || "viewer",
    client_ids: user?.client_ids || [],
  });

  const toggleClient = (id) => {
    const s = new Set(form.client_ids);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setForm({ ...form, client_ids: Array.from(s) });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4"
      onClick={onCancel}
    >
      <div
        className="bg-white border border-zinc-200 rounded-lg w-full max-w-md p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-heading text-lg font-bold mb-3">
          {isEdit ? `Edit ${user.username}` : "New user"}
        </h2>
        <form
          data-testid="user-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!isEdit && !form.username.trim()) return toast.error("Username is required");
            if (!isEdit && !form.password) return toast.error("Password is required");
            const payload = isEdit
              ? { role: form.role, ...(form.password ? { password: form.password } : {}) }
              : { username: form.username.trim(), password: form.password, role: form.role };
            if (!isEdit && form.role === "operator" && form.client_ids.length > 0) {
              payload.client_ids = form.client_ids;
            }
            onSave(payload);
          }}
          className="space-y-3"
        >
          {!isEdit && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
                Username
              </span>
              <input
                data-testid="user-input-username"
                className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                autoComplete="off"
              />
            </label>
          )}
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
              {isEdit ? "New password (leave blank to keep)" : "Password"}
            </span>
            <input
              data-testid="user-input-password"
              type="password"
              className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
              Role
            </span>
            <select
              data-testid="user-input-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>

          {form.role === "operator" && !isEdit && (
            <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50/60">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
                Initial client scope
              </div>
              {!clients.data?.length ? (
                <div className="text-xs text-zinc-500">No clients yet — you can assign them later from the Scopes tab.</div>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1.5">
                  {clients.data.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.client_ids.includes(String(c.id))}
                        onChange={() => toggleClient(String(c.id))}
                      />
                      <span>{c.name}</span>
                      <span className="text-[10px] text-zinc-400 font-mono ml-auto">{c.id}</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-zinc-500 mt-2">
                Tip: you can also assign scopes later in the <strong>Operator scopes</strong> tab.
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              data-testid="user-save"
              type="submit"
              className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tab 2 — Operator scope assignments
// -----------------------------------------------------------------------------
function ScopesTab() {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: usersApi.list });
  const clients = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });

  const operators = useMemo(
    () => (users.data || []).filter((u) => u.role === "operator"),
    [users.data]
  );
  const [selectedId, setSelectedId] = useState(null);
  const selected = operators.find((u) => u.id === selectedId) || operators[0];

  // Local editing draft — resets whenever the selected operator changes.
  const [draft, setDraft] = useState(new Set());
  useEffect(() => {
    setDraft(new Set((selected?.client_ids || []).map(String)));
  }, [selected?.id, selected?.client_ids]);

  const setClientsM = useMutation({
    mutationFn: ({ id, client_ids }) => usersApi.setClients(id, client_ids),
    onSuccess: () => {
      toast.success("Scope updated");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const toggle = (id) => {
    const s = new Set(draft);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setDraft(s);
  };

  const dirty = useMemo(() => {
    const original = new Set((selected?.client_ids || []).map(String));
    if (original.size !== draft.size) return true;
    for (const v of draft) if (!original.has(v)) return true;
    return false;
  }, [draft, selected]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-zinc-200 flex items-center gap-2">
          <Shield className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            operators
          </span>
        </div>
        {operators.length === 0 ? (
          <div className="p-4 text-xs text-zinc-500" data-testid="scopes-empty">
            No operators yet. Create one from the <strong>Users</strong> tab.
          </div>
        ) : (
          <ul>
            {operators.map((u) => (
              <li key={u.id || u.username}>
                <button
                  data-testid={`scope-user-${u.username}`}
                  onClick={() => setSelectedId(u.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 ${
                    selected?.id === u.id ? "bg-zinc-50 border-l-2 border-[#3730A3]" : ""
                  }`}
                >
                  <div className="font-medium">{u.username}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">
                    {(u.client_ids || []).length} clients
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-lg">
        {!selected ? (
          <div className="p-8 text-center text-sm text-zinc-500">
            Pick an operator from the left to edit their client scope.
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                  editing scope
                </div>
                <div className="font-heading text-base font-semibold mt-0.5">
                  {selected.username}
                </div>
              </div>
              <button
                data-testid="scope-save"
                disabled={!dirty || setClientsM.isPending}
                onClick={() =>
                  setClientsM.mutate({ id: selected.id, client_ids: Array.from(draft) })
                }
                className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900 disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                <Save className="w-3.5 h-3.5" />
                {setClientsM.isPending ? "Saving…" : "Save scope"}
              </button>
            </div>
            <div className="p-4">
              {clients.data?.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {clients.data.map((c) => {
                    const id = String(c.id);
                    const checked = draft.has(id);
                    return (
                      <label
                        key={id}
                        data-testid={`scope-client-${id}`}
                        className={`flex items-center gap-3 border rounded-md px-3 py-2 text-sm cursor-pointer transition ${
                          checked
                            ? "border-zinc-900 bg-zinc-50"
                            : "border-zinc-200 hover:border-zinc-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{c.name}</div>
                          <div className="text-[10px] text-zinc-500 font-mono truncate">
                            {[c.city, c.country].filter(Boolean).join(", ") || "—"}
                          </div>
                        </div>
                        <div className="text-[10px] text-zinc-400 font-mono">{id}</div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-zinc-500">No clients defined.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
